// One sub-tab per watch (+ "Watch something new"), down the side on the
// Watches page or across the top of the Briefing page. The reader's daily
// briefing is a watch too (kanban 001b6e2b) and reads as the standing tab; a
// `leading` tab can stand in for it ("Start my daily briefing"). Every tab
// carries a data-action for the usage graph.
import { railLabel, watchStatusLine } from '../../pages/merlinWatchesUtils';
import type { Watch } from '../../services/merlinWatches';
import { NEW_WATCH } from './useWatches';
import styles from './watches.module.css';

export interface RailLeadTab {
  id: string;
  label: string;
  meta?: string;
  status?: string;
  /** The recorder action name for this tab (e.g. `briefing_start_tab`). */
  action: string;
}

export interface WatchRailProps {
  items: Watch[];
  selected: string | null;
  onSelect: (id: string) => void;
  /** Prefix each watch with its owner (the admin's "everyone's" view). */
  showOwner?: boolean;
  layout?: 'side' | 'top';
  /** Tabs rendered before the watches (the Briefing page's "Start my daily
   * briefing" while the reader has none). */
  leading?: RailLeadTab[];
  ariaLabel?: string;
}

export function WatchRail({
  items,
  selected,
  onSelect,
  showOwner = false,
  layout = 'side',
  leading = [],
  ariaLabel = 'Your watches',
}: WatchRailProps) {
  return (
    <nav
      className={`${styles.rail} ${layout === 'top' ? styles.railTop : ''}`}
      aria-label={ariaLabel}
      role="tablist"
    >
      {leading.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={selected === tab.id}
          aria-label={tab.label}
          className={`${styles.railItem} ${styles.leadTab} ${selected === tab.id ? styles.railItemActive : ''}`}
          onClick={() => onSelect(tab.id)}
          data-action={tab.action}
          data-outcome="open"
        >
          <span className={styles.railSubject}>{tab.label}</span>
          {tab.meta ? <span className={styles.railMeta}>{tab.meta}</span> : null}
          {tab.status ? <span className={styles.railStatus}>{tab.status}</span> : null}
        </button>
      ))}
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={selected === item.id}
          aria-label={railLabel(item, showOwner)}
          title={item.kind === 'briefing' ? undefined : item.subject}
          className={`${styles.railItem} ${item.kind === 'briefing' ? styles.leadTab : ''} ${selected === item.id ? styles.railItemActive : ''}`}
          onClick={() => onSelect(item.id)}
          data-action={item.kind === 'briefing' ? 'briefing_tab' : 'watch_tab'}
          data-target-watch-id={item.id}
          data-outcome="open"
        >
          <span className={styles.railSubject}>{railLabel(item, showOwner)}</span>
          <span className={styles.railMeta}>
            {showOwner && item.owner_name ? `${item.owner_name} · ` : ''}
            {item.kind === 'briefing' && showOwner ? 'Daily briefing · ' : ''}
            {item.cadence_label}
            {item.status === 'paused' ? ' · paused' : ''}
          </span>
          <span className={`${styles.railStatus} ${item.open_run ? styles.railStatusBusy : ''}`}>
            {watchStatusLine(item)}
          </span>
        </button>
      ))}
      <button
        type="button"
        role="tab"
        aria-selected={selected === NEW_WATCH}
        className={`${styles.railNew} ${selected === NEW_WATCH ? styles.railItemActive : ''}`}
        onClick={() => onSelect(NEW_WATCH)}
        data-action="watch_new_tab"
        data-outcome="open"
      >
        + Watch something new
      </button>
    </nav>
  );
}
